import { useState, useEffect, useMemo } from 'react';
import { DatePickerInput, Button } from 'pepsico-ds';
import { useUrlParams } from '@/hooks/useUrlParams';
import { formatDateForUrl } from '@/helpers/formatDateForUrl';

const parseDateFromUrl = dateString => {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return null;
  }

  // Parse the date parts directly to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);

  // Create date with local timezone interpretation (without 'Z')
  const date = new Date(year, month - 1, day);
  return !isNaN(date.getTime()) ? date : null;
};

const DateRangeSelector = ({
  onDatesChange,
  isDisabled = false,
  startLabel = 'Store Creation Start Date',
  endLabel = 'Store Creation End Date',
  startDateKey = 'startDate', // Allow customizing URL param keys
  endDateKey = 'endDate', // Allow customizing URL param keys
  initialStartDate = null,
  initialEndDate = null
}) => {
  const [error, setError] = useState(null);

  // Manage URL state for start and end dates
  const { params: urlParams, setParams: setUrlParams } = useUrlParams([
    startDateKey,
    endDateKey
  ]);

  // Use initialStartDate and initialEndDate on component mount if URL params aren't set
  useEffect(() => {
    const hasStartDate =
      urlParams[startDateKey] !== undefined && urlParams[startDateKey] !== null;
    const hasEndDate =
      urlParams[endDateKey] !== undefined && urlParams[endDateKey] !== null;

    if (initialStartDate && !hasStartDate && initialEndDate && !hasEndDate) {
      setUrlParams({
        [startDateKey]: formatDateForUrl(initialStartDate),
        [endDateKey]: formatDateForUrl(initialEndDate)
      });
    } else if (initialStartDate && !hasStartDate) {
      setUrlParams({
        ...urlParams,
        [startDateKey]: formatDateForUrl(initialStartDate)
      });
    } else if (initialEndDate && !hasEndDate) {
      setUrlParams({
        ...urlParams,
        [endDateKey]: formatDateForUrl(initialEndDate)
      });
    }
  }, [
    initialStartDate,
    initialEndDate,
    startDateKey,
    endDateKey,
    setUrlParams,
    urlParams
  ]);

  // Derive Date objects from URL string values
  const startDate = useMemo(
    () => parseDateFromUrl(urlParams[startDateKey]),
    [urlParams, startDateKey]
  );
  const endDate = useMemo(
    () => parseDateFromUrl(urlParams[endDateKey]),
    [urlParams, endDateKey]
  );

  useEffect(() => {
    // Notify parent component when dates derived from URL change
    const validationError = validateDates(startDate, endDate);
    setError(validationError);

    onDatesChange({ startDate, endDate, isValid: !validationError });
  }, [startDate, endDate, onDatesChange]);

  const validateDates = (start, end) => {
    // Clear error if either date is null
    if (!start || !end) {
      return null; // Return null if no error
    }

    // Check if end date is before start date
    if (end < start) {
      return 'End date must be after start date';
    }

    // No error
    return null;
  };

  // Wrapper function for start date changes
  const handleStartDateChange = dateValue => {
    // Just use the dateValue directly without UTC conversion
    const newStartDate = dateValue ? new Date(dateValue) : null;

    if (newStartDate && isNaN(newStartDate.getTime())) {
      console.error('Invalid start date format:', dateValue);
      // Keep existing end date, set start date to null in URL if format is invalid
      setUrlParams({
        [startDateKey]: null,
        [endDateKey]: formatDateForUrl(endDate)
      });
      setError(null); // Clear validation error if format is bad
      return;
    }

    const validationError = validateDates(newStartDate, endDate);
    setError(validationError);

    // Always update the URL with the selected start date and the current end date
    setUrlParams({
      [startDateKey]: formatDateForUrl(newStartDate),
      [endDateKey]: formatDateForUrl(endDate) // Keep existing end date format
    });
  };

  // Wrapper function for end date changes
  const handleEndDateChange = dateValue => {
    // Just use the dateValue directly without UTC conversion
    const newEndDate = dateValue ? new Date(dateValue) : null;

    if (newEndDate && isNaN(newEndDate.getTime())) {
      console.error('Invalid end date format:', dateValue);
      // Keep existing start date, set end date to null in URL if format is invalid
      setUrlParams({
        [startDateKey]: formatDateForUrl(startDate),
        [endDateKey]: null
      });
      setError(null); // Clear validation error if format is bad
      return;
    }

    const validationError = validateDates(startDate, newEndDate);
    setError(validationError);

    // Always update the URL with the current start date and the selected end date
    setUrlParams({
      [startDateKey]: formatDateForUrl(startDate), // Keep existing start date format
      [endDateKey]: formatDateForUrl(newEndDate)
    });
  };

  const handleClearDates = () => {
    // Clear both date params in the URL
    setUrlParams({ [startDateKey]: null, [endDateKey]: null });

    // Explicitly call onDatesChange with null values
    onDatesChange({ startDate: null, endDate: null, isValid: true });
  };

  return (
    <div className='flex flex-col w-full'>
      <div className='flex flex-row w-full gap-8 pt-4 pb-4 items-end date-range-selector-container'>
        <DatePickerInput
          label={startLabel}
          isDark={true}
          className='!w-[300px]'
          selected={startDate}
          setSelected={handleStartDateChange}
          disabled={isDisabled}
        />

        <DatePickerInput
          label={endLabel}
          isDark={true}
          className='!w-[300px]'
          selected={endDate}
          setSelected={handleEndDateChange}
          disabled={isDisabled}
        />

        {(startDate || endDate) && (
          <div className='date-range-selector-clear-button-container'>
            <Button isDark={true} variant='secondary' onClick={handleClearDates}>
              Clear All Dates
            </Button>
          </div>
        )}
      </div>

      {error && <div className='text-red-500 text-sm mt-1 ml-1'>{error}</div>}
    </div>
  );
};

export default DateRangeSelector;
